import type { SourceFile, SourceNode } from "../sources-types";
import { isFolder } from "../sources-types";
import type { UpdateSourcePayload } from "../../../tars-hooks/useUpdateSource";

/** Convert store parent id (string | null) to API parent_id (number | null). */
export function toParentIdNum(parentId: string | null): number | null {
  if (parentId == null || parentId === "") return null;
  const n = Number(parentId);
  return Number.isNaN(n) ? null : n;
}

/** Parse the notebookId URL param (string) into a number, or null if invalid. */
export function parseNotebookId(raw: string | undefined): number | null {
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

/** API `type` string for tars/source/update (aligned with upload / list). */
export function tarsSourceTypeForApi(node: SourceNode): string {
  if (isFolder(node)) return "folder";
  const f = node as SourceFile;
  if (f.type === "quickNote") return "quick_note";
  if (f.type === "dataRepo") return "es_index";
  if (f.type === "dataBank") return "data_bank";
  return f.type;
}

/** `more` map from client node so updates preserve link URL, data repo index, etc. */
export function defaultMoreFromSourceNode(node: SourceNode): Record<string, unknown> {
  if (isFolder(node)) return {};
  const f = node as SourceFile;
  const m: Record<string, unknown> = {};
  if (f.url != null && f.url !== "") m.url = f.url;
  if (f.es_index != null && f.es_index !== "") m.es_index = f.es_index;
  if (f.extension != null && f.extension !== "") m.extension = f.extension;
  if (f.type === "dataBank") {
    if (f.data_bank_fst_key) m.fst_key = f.data_bank_fst_key;
    if (f.data_bank_fsd_id != null) m.fsd_id = f.data_bank_fsd_id;
    if (f.data_bank_view_type) m.view_type = f.data_bank_view_type;
    if (f.data_bank_election) m.election_label = f.data_bank_election;
    if (f.data_bank_map_key) m.map_key = f.data_bank_map_key;
  }
  return m;
}

export function buildUpdateSourcePayload(
  node: SourceNode,
  notebookId: number,
  sourceId: number,
  name: string,
  morePatch?: Record<string, unknown>
): UpdateSourcePayload {
  /** Update API expects integer parent_id; root uses 0 (not null). */
  const parent_id = toParentIdNum(node.parentId) ?? 0;
  const type = tarsSourceTypeForApi(node);
  let s3_key = "";
  let mime_type = "";
  let size_bytes = 0;
  if (!isFolder(node)) {
    const f = node as SourceFile;
    if (f.s3_key) s3_key = f.s3_key;
  }
  const more = { ...defaultMoreFromSourceNode(node), ...morePatch };
  return {
    notebook_id: notebookId,
    type,
    parent_id,
    s3_key,
    name,
    mime_type,
    size_bytes,
    tags: [],
    more,
    source_id: sourceId,
  };
}

/** S3 bucket used for TARS notebook sources. */
export const TARS_SOURCES_BUCKET = "urn-assets";

/** Base S3 prefix for a notebook's sources (no trailing slash). */
const TARS_SOURCES_PREFIX = "tars/notebooks";

/** Full S3 directory path for uploads: base/sources or base/sources/Folder A/Folder B. */
export function getTarsS3DirPath(notebookId: number, folderPath: string): string {
  const base = `${TARS_SOURCES_PREFIX}/${notebookId}/sources`;
  const trimmed = folderPath?.trim() ?? "";
  return trimmed ? `${base}/${trimmed}` : base;
}
